import BaseSearchController from "./base_search_controller"

const PRODUCT_PER_PAGE = 20

export default class extends BaseSearchController {
  static targets = [
    "centerPoint",
    "form",
    "map",
    "productInfoWindow",
    "productList",
    "productListItem",
    "results",
    "queryInfo",
    "noResultsMessage",
    "modalBody",
    "mobileFilterButton",
  ]

  async resultsTargetConnected() {
    this.scrollResultsToTop()
  }

  // InfoBox sizing for activity markers (overrides base hook).
  // Used for both individual marker clicks and clustered marker clicks.
  // Width 200px centered + 22px above the marker.
  infoBoxPixelOffset() {
    return new google.maps.Size(-200 / 2, -22)
  }

  // Tracking for clustered activity pins (overrides base hook)
  trackClusterMapPin(targetMarkerInfoWindow) {
    const { activityId, activityBoost } = targetMarkerInfoWindow.dataset
    if (this.trackedMapMarkerPinsList.indexOf(activityId) == -1) {
      analytics.track("Activity Search Map Pin", { activity_id: activityId, activity_boost: activityBoost })
      this.trackedMapMarkerPinsList.push(activityId)
    }
  }

  addMapListeners() {
    super.addMapListeners()

    google.maps.event.addListener(this.map, "idle", () => {
      if (this.mapMovedByUser && !this.boundsFitted) {
        this.resetFormElementAddress()
        this.searchFromMap()
        this.mapMovedByUser = false

        if (!this.hasTrackedMapMoved) {
          analytics.track("Activity Search Map Moved")
          this.hasTrackedMapMoved = true
        }
      } else {
        this.boundsFitted = false
      }
    })
  }

  // Add an infoWindow to a marker that displays a productInfoWindow
  // Close the infoWindow if another marker is clicked
  addInfoWindow(marker, productInfoWindow) {
    marker.addListener("click", () => {
      const { activityId, activityBoost } = productInfoWindow.dataset
      if (this.trackedMapMarkerPinsList.indexOf(activityId) == -1) {
        analytics.track("Activity Search Map Pin", { activity_id: activityId, activity_boost: activityBoost })
        this.trackedMapMarkerPinsList.push(productInfoWindow.dataset.activityId)
      }
    })

    marker.addListener("click", () => {
      // Close the infoWindow if the clicked marker is the same as the previous one
      if (this.infoBox?.position_ == marker.position) {
        this.infoBox?.close()
        this.infoBox = null
      } else {
        const infoWindowContent = productInfoWindow.children[0].cloneNode(true)

        infoWindowContent.addEventListener("click", (e) => {
          this.trackClickedActivity(e, { source: "map" })
        })

        // Close the previous infoWindow if it exists
        if (this.infoBox) this.infoBox?.close()

        // Reuse the infoWindow of the clicked marker if exists
        // Else create a new infoWindow
        if (marker.infoBox) {
          this.infoBox = marker.infoBox
          this.infoBox.open(this.map, this.marker)
        } else {
          this.infoBox = new InfoBox({
            alignBottom: true,
            pixelOffset: this.infoBoxPixelOffset(),
            infoBoxClearance: new google.maps.Size(10, 50),
            visible: true,
            closeBoxURL: "",
            zIndex: 10000,
          })
          this.infoBox.setContent(infoWindowContent)
          this.infoBox.setPosition(marker.position)
          this.infoBox.open(this.map, this.marker)
          marker.infoBox = this.infoBox
        }

        // Scroll the activity list to the corresponding activity
        // Highlight the activity in the list with a different color
        this.productListItemTargets.forEach((e) => e.children[0].classList.remove("pinned"))
        productInfoWindow.previousElementSibling.children[0].classList.add("pinned")
        productInfoWindow.previousElementSibling.scrollIntoView({
          behavior: "smooth",
          block: "center",
        })
      }
    })
  }

  // Tracking Methods
  trackClickedActivity(event, params = {}) {
    const isArrowClicked = event.target.closest(".swiper .control")
    const isButtonClicked = event.target.closest("button")

    if (isArrowClicked || isButtonClicked) {
      return
    }

    const element = event.currentTarget.querySelector(
      ".c--activity-list-item, .c--activity--info-window"
    )
    const { activityId, position, page, activityBoost } = element.dataset
    const totalPosition = this.productPosition(position, page)
    const properties = {
      activity_id: activityId,
      activity_boost: activityBoost,
      position: totalPosition,
      source: "list",
      ...params,
    }
    analytics.track("Search Result Clicked", properties)
  }

  trackVisibleActivities() {
    this.productListItemTargets.forEach((card) => {
      const { activityId, position, page, activityBoost } = card.children[0].dataset
      if(
        this.isScrolledIntoView &&
        this.isScrolledIntoView(card) &&
        this.trackedProductList.indexOf(activityId) == -1
      ) {
        const totalPosition = this.productPosition(position, page)
        const properties = {
          activity_id: activityId,
          activity_boost: activityBoost,
          position: totalPosition,
          page,
          source: "listing",
        }
        analytics.track("Viewed Search Result", properties)
        this.trackedProductList.push(activityId)
      }
    })
  }

  productPosition(position, page) {
    if (!position || !page) return
    return (page - 1) * PRODUCT_PER_PAGE + (Number(position) + 1)
  }
}
